// QueueAnnouncer — voice announcements for the v2 board.
//
// A SINGLE shared queue across every selected doctor, so announcements play
// strictly one after another (no overlap when several doctors already have an
// ongoing patient on first load). Each announcement carries its own doctor
// name. Announces for all selected doctors, not just the visible slide.
import React, { useMemo } from "react";
import { useQueueVoice, VoiceAnnouncement } from "../hooks/useQueueVoice";
import AudioPermissionModal from "./AudioPermissionModal";
import { getAudioLanguage } from "../utils/audioLanguage";
import { useFeatureFlags } from "../../lib/contexts/FeatureFlagContext";
import { useTVConfig } from "../contexts/TVConfigContext";
import { Doctor } from "../../lib/utils/types";
import { BookingV2 } from "../types";
import { isOngoingStatus } from "../constants";

const QueueAnnouncer: React.FC<{
  doctors: Doctor[];
  bookingsByMapping: Record<string, BookingV2[]>;
}> = ({ doctors, bookingsByMapping }) => {
  const { announce_first_name_tv } = useFeatureFlags();
  const tvConfig = useTVConfig();
  const nameHidden = tvConfig.patient_name === false;

  // Flatten every doctor's ongoing patients into one ordered list.
  const announcements = useMemo<VoiceAnnouncement[]>(() => {
    const out: VoiceAnnouncement[] = [];
    for (const doctor of doctors) {
      const bookings = bookingsByMapping[doctor.mapping_id] ?? [];
      for (const b of bookings) {
        if (!isOngoingStatus(b.status_id ?? b.status)) continue;
        // What to say: token when names are hidden; otherwise the patient name
        // (first-name only when the flag is set). Visual star-masking is not
        // applied to speech — you can't read stars aloud.
        let patientName: string;
        if (nameHidden) {
          patientName = b.token_number ? `Token ${b.token_number}` : "";
        } else {
          const raw = b.full_name || "";
          patientName = announce_first_name_tv
            ? raw.trim().split(/\s+/)[0] ?? ""
            : raw;
        }
        out.push({
          bookingId: b.booking_id,
          doctorName: doctor.full_name,
          patientName,
          transferCycleId: b.recent_transfer_id ?? undefined,
          room: b.room_name || undefined,
        });
      }
    }
    return out;
  }, [doctors, bookingsByMapping, nameHidden, announce_first_name_tv]);

  const { permissionNeeded, allow, deny } = useQueueVoice(
    getAudioLanguage(),
    announcements
  );

  return (
    <AudioPermissionModal
      isOpen={permissionNeeded}
      onAllow={allow}
      onCancel={deny}
    />
  );
};

export default QueueAnnouncer;
